import axios from 'axios';
import { delay } from 'q';
import { stringify } from 'querystring';

import { addAuthHeader } from '../utils/axios.utils';
import { environment } from './environment';

interface InterceptorConfig {
  accessToken: string;
  tokenRefresh: () => Promise<any>;
}

let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;

export const httpClient = axios.create({
  baseURL: environment.BASE_API_URL,
  paramsSerializer: (params) => stringify(params),
  withCredentials: true,
});

httpClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.request.responseType === 'blob') {
      const errorMessage: any = await fileReader(error.response.data);

      const data = JSON.parse(errorMessage);

      const newError = {
        ...error,
        response: {
          ...error.response,
          data,
        },
      };
      return Promise.reject(newError);
    }
    return Promise.reject(error);
  },
);

export const addAuthInterceptor = (interceptorConfig: InterceptorConfig) => {
  removeAuthInterceptor();

  requestInterceptorId = httpClient.interceptors.request.use(
    (config) => {
      addAuthHeader(config, interceptorConfig.accessToken);

      return config;
    },
    (response) => {
      return Promise.reject(response);
    },
  );

  responseInterceptorId = httpClient.interceptors.response.use(
    (response) => response,
    ({ config, response }) => {
      if (config && response && response.status === 401) {
        interceptorConfig.tokenRefresh();

        /*
         * wait long before rejecting so that side effects (error notification) would not happen before token refresh
         */
        const forever = 60000;
        return delay(forever).then(() => Promise.reject(response));
      } else {
        return Promise.reject(response);
      }
    },
  );
};

export const removeAuthInterceptor = () => {
  if (requestInterceptorId) {
    httpClient.interceptors.request.eject(requestInterceptorId);
  }

  if (responseInterceptorId) {
    httpClient.interceptors.response.eject(responseInterceptorId);
  }
};

async function fileReader(file: Blob) {
  const fileReader = new FileReader();

  return new Promise((resolve, reject) => {
    fileReader.onerror = () => {
      fileReader.abort();
      reject(new Error('problemParsingFile'));
    };

    fileReader.onload = () => {
      resolve(fileReader.result);
    };

    fileReader.readAsText(file);
  });
}
